import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Eye, EyeOff, Mail, Lock, User, Sparkles, AlertCircle, Loader2, ArrowLeft, Image as ImageIcon, Mic, FileText, Check, Zap, Shield, Globe } from "lucide-react";
import { Logo } from "./Logo";

interface AuthModalProps {
  mode: 'login' | 'register';
  onClose: () => void;
  onModeChange: (mode: 'login' | 'register') => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ mode, onClose, onModeChange }) => {
  const { signIn, signUp } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  
  const [loginForm, setLoginForm] = useState({
    email: '',
    password: ''
  });
  
  const [registerForm, setRegisterForm] = useState({
    name: '',
    email: '',
    password: ''
  });

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleLoginChange = (field: string, value: string) => {
    setLoginForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleRegisterChange = (field: string, value: string) => {
    setRegisterForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const newErrors: {[key: string]: string} = {};
    if (!loginForm.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(loginForm.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!loginForm.password.trim()) {
      newErrors.password = 'Password is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoginLoading(true);
    try {
      const { error } = await signIn(loginForm.email.trim(), loginForm.password);
      if (!error) {
        onClose();
      }
    } catch (error: any) {
      console.error('Login error:', error);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const newErrors: {[key: string]: string} = {};
    if (!registerForm.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!registerForm.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(registerForm.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!registerForm.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (registerForm.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters long';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setRegisterLoading(true);
    try {
      const { error } = await signUp(registerForm.email.trim(), registerForm.password, registerForm.name.trim());
      if (!error) {
        onClose();
      }
    } catch (error: any) {
      console.error('Registration error:', error);
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`
        }
      });

      if (error) {
        throw error;
      }

      toast.success('Redirecting to Google...');
      onClose();
    } catch (error: any) {
      console.error('Google sign-in error:', error);
      toast.error(error.message || 'Google sign-in failed. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!forgotPasswordEmail.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    
    if (!validateEmail(forgotPasswordEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setForgotPasswordLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotPasswordEmail.trim(), {
        redirectTo: `${window.location.origin}/`
      });

      if (error) {
        throw error;
      }

      toast.success('Password reset email sent! Check your inbox.');
      setShowForgotPassword(false);
      setForgotPasswordEmail('');
    } catch (error: any) {
      console.error('Forgot password error:', error);
      toast.error(error.message || 'Failed to send reset email. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const isLoading = loginLoading || registerLoading || googleLoading || forgotPasswordLoading;

  if (showForgotPassword) {
    return (
      <div className="fixed inset-0 bg-background/80 backdrop-blur-xl z-50 animate-fade-in overflow-y-auto">
        <div className="min-h-full flex items-center justify-center p-4 py-8">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-primary/20 via-transparent to-transparent rounded-full blur-3xl animate-pulse" />
            <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-pink-500/20 via-transparent to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          </div>
          
          <Card className="w-full max-w-md animate-scale-in border-0 shadow-2xl bg-card/95 backdrop-blur-xl relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-pink-500 to-primary" />
            
            <CardContent className="p-6 sm:p-8 space-y-6">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowForgotPassword(false)}
                className="absolute left-4 top-4 h-10 w-10 rounded-full hover:bg-muted"
                disabled={isLoading}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>

              <div className="text-center pt-8 space-y-3">
                <div className="mx-auto w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-primary to-pink-500 rounded-2xl flex items-center justify-center shadow-lg shadow-primary/30">
                  <Mail className="w-7 h-7 sm:w-8 sm:h-8 text-primary-foreground" />
                </div>
                <h2 className="text-xl sm:text-2xl font-bold">Reset Password</h2>
                <p className="text-sm sm:text-base text-muted-foreground">Enter your email to receive a password reset link</p>
              </div>

              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email" className="text-sm font-semibold">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="Enter your email"
                      value={forgotPasswordEmail}
                      onChange={(e) => setForgotPasswordEmail(e.target.value)}
                      className="pl-12 h-12 rounded-xl border-2 border-border focus:border-primary transition-colors"
                      disabled={isLoading}
                    />
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  className="w-full h-12 bg-gradient-to-r from-primary to-pink-500 hover:from-primary/90 hover:to-pink-500/90 text-primary-foreground rounded-xl shadow-lg shadow-primary/30 transition-all duration-300 hover:scale-[1.02]"
                  disabled={isLoading}
                >
                  {forgotPasswordLoading ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="animate-spin h-5 w-5" />
                      <span>Sending...</span>
                    </div>
                  ) : (
                    <span>Send Reset Link</span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-xl z-50 animate-fade-in overflow-y-auto">
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-primary/20 via-transparent to-transparent rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-pink-500/20 via-transparent to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-gradient-to-br from-blue-500/10 to-transparent rounded-full blur-3xl" />
      </div>

      <div className="min-h-full flex items-center justify-center p-4 py-6 sm:py-8">
        <Card className="w-full max-w-5xl animate-scale-in border-0 shadow-2xl bg-card/95 backdrop-blur-xl relative">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-pink-500 to-primary" />
          
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="absolute right-3 top-3 sm:right-4 sm:top-4 z-20 h-9 w-9 sm:h-10 sm:w-10 rounded-full hover:bg-muted"
            disabled={isLoading}
          >
            <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
          </Button>

          <div className="grid lg:grid-cols-2">
            {/* Left Panel - Branding */}
            <div className="hidden lg:flex flex-col justify-between p-10 bg-gradient-to-br from-primary via-purple-600 to-pink-500 text-primary-foreground relative overflow-hidden rounded-l-lg">
              {/* Animated shapes */}
              <div className="absolute inset-0 overflow-hidden">
                <div className="absolute top-20 left-10 w-32 h-32 bg-white/10 rounded-full blur-xl animate-pulse" />
                <div className="absolute bottom-20 right-10 w-48 h-48 bg-white/10 rounded-full blur-xl animate-pulse" style={{ animationDelay: '0.5s' }} />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-white/5 rounded-full blur-2xl" />
              </div>

              <div className="relative z-10">
                <Logo size="lg" showText={false} className="mb-6 [&_img]:brightness-0 [&_img]:invert" />
                <h1 className="text-4xl font-bold mb-4">Welcome to Genova AI</h1>
                <p className="text-xl text-white/90">Your complete AI content creation platform</p>
              </div>

              <div className="relative z-10 space-y-6">
                <div className="flex items-start gap-4 p-4 bg-white/10 backdrop-blur-sm rounded-2xl">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <ImageIcon className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">AI Image Generation</h3>
                    <p className="text-white/80 text-sm">Create stunning visuals with state-of-the-art AI</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-white/10 backdrop-blur-sm rounded-2xl">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mic className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Voice Synthesis</h3>
                    <p className="text-white/80 text-sm">Natural voices in 29+ languages</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 bg-white/10 backdrop-blur-sm rounded-2xl">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FileText className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">AI Script Writing</h3>
                    <p className="text-white/80 text-sm">Professional scripts for any content</p>
                  </div>
                </div>
              </div>

              <div className="relative z-10 flex items-center gap-6 text-sm text-white/70">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  <span>Secure</span>
                </div>
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  <span>Fast</span>
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  <span>Global</span>
                </div>
              </div>
            </div>

            {/* Right Panel - Auth Forms */}
            <CardContent className="p-5 sm:p-6 lg:p-10">
              <div className="max-w-md mx-auto space-y-5 sm:space-y-6">
                <div className="lg:hidden text-center space-y-2 mb-6 sm:mb-8">
                  <Logo size="sm" className="justify-center mb-3 sm:mb-4" />
                  <h2 className="text-xl sm:text-2xl font-bold">Welcome to Genova AI</h2>
                </div>

              <Tabs value={mode} onValueChange={(value) => onModeChange(value as 'login' | 'register')} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6 sm:mb-8 bg-muted p-1 sm:p-1.5 rounded-xl sm:rounded-2xl h-12 sm:h-14">
                  <TabsTrigger 
                    value="login" 
                    className="data-[state=active]:bg-card data-[state=active]:shadow-lg font-semibold transition-all duration-300 rounded-lg sm:rounded-xl text-sm sm:text-base"
                  >
                    Sign In
                  </TabsTrigger>
                  <TabsTrigger 
                    value="register" 
                    className="data-[state=active]:bg-card data-[state=active]:shadow-lg font-semibold transition-all duration-300 rounded-lg sm:rounded-xl text-sm sm:text-base"
                  >
                    Sign Up
                  </TabsTrigger>
                </TabsList>
                
                {/* Social Login */}
                <div className="space-y-3 sm:space-y-4 mb-5 sm:mb-6">
                  <Button 
                    onClick={handleGoogleSignIn}
                    disabled={isLoading}
                    variant="outline"
                    className="w-full h-12 sm:h-14 border-2 hover:bg-muted transition-all duration-300 font-semibold rounded-xl text-sm sm:text-base"
                  >
                    {googleLoading ? (
                      <div className="flex items-center gap-2">
                        <Loader2 className="animate-spin h-4 w-4 sm:h-5 sm:w-5" />
                        <span>Connecting...</span>
                      </div>
                    ) : (
                      <>
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        <span>Continue with Google</span>
                      </>
                    )}
                  </Button>
                  
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-card px-3 sm:px-4 text-muted-foreground font-medium">
                        Or continue with email
                      </span>
                    </div>
                  </div>
                </div>
                
                <TabsContent value="login" className="mt-0">
                  <form onSubmit={handleLogin} className="space-y-4 sm:space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="login-email" className="text-sm font-semibold">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="name@example.com"
                          value={loginForm.email}
                          onChange={(e) => handleLoginChange('email', e.target.value)}
                          className={`pl-10 sm:pl-12 h-12 sm:h-14 rounded-xl border-2 transition-colors text-sm sm:text-base ${errors.email ? 'border-destructive' : 'border-border focus:border-primary'}`}
                          disabled={isLoading}
                        />
                        {errors.email && (
                          <div className="flex items-center mt-2 text-destructive text-xs font-medium">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {errors.email}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="login-password" className="text-sm font-semibold">Password</Label>
                        <Button
                          type="button"
                          variant="link"
                          className="text-xs sm:text-sm text-primary hover:text-primary/80 p-0 h-auto font-medium"
                          onClick={() => setShowForgotPassword(true)}
                        >
                          Forgot password?
                        </Button>
                      </div>
                      <div className="relative">
                        <Lock className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
                        <Input
                          id="login-password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Enter your password"
                          value={loginForm.password}
                          onChange={(e) => handleLoginChange('password', e.target.value)}
                          className={`pl-10 sm:pl-12 pr-10 sm:pr-12 h-12 sm:h-14 rounded-xl border-2 transition-colors text-sm sm:text-base ${errors.password ? 'border-destructive' : 'border-border focus:border-primary'}`}
                          disabled={isLoading}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 h-9 w-9 sm:h-10 sm:w-10 rounded-lg hover:bg-muted"
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={isLoading}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" /> : <Eye className="h-4 w-4 sm:h-5 sm:w-5" />}
                        </Button>
                        {errors.password && (
                          <div className="flex items-center mt-2 text-destructive text-xs font-medium">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {errors.password}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full h-12 sm:h-14 bg-gradient-to-r from-primary to-pink-500 hover:from-primary/90 hover:to-pink-500/90 text-primary-foreground rounded-xl shadow-lg shadow-primary/30 transition-all duration-300 hover:scale-[1.02] text-sm sm:text-base font-semibold"
                      disabled={isLoading}
                    >
                      {loginLoading ? (
                        <div className="flex items-center gap-2">
                          <Loader2 className="animate-spin h-4 w-4 sm:h-5 sm:w-5" />
                          <span>Signing In...</span>
                        </div>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                          <span>Sign In</span>
                        </>
                      )}
                    </Button>
                  </form>
                </TabsContent>
                
                <TabsContent value="register" className="mt-0">
                  <form onSubmit={handleRegister} className="space-y-4 sm:space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="register-name" className="text-sm font-semibold">Full Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
                        <Input
                          id="register-name"
                          type="text"
                          placeholder="John Doe"
                          value={registerForm.name}
                          onChange={(e) => handleRegisterChange('name', e.target.value)}
                          className={`pl-10 sm:pl-12 h-12 sm:h-14 rounded-xl border-2 transition-colors text-sm sm:text-base ${errors.name ? 'border-destructive' : 'border-border focus:border-primary'}`}
                          disabled={isLoading}
                        />
                        {errors.name && (
                          <div className="flex items-center mt-2 text-destructive text-xs font-medium">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {errors.name}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="register-email" className="text-sm font-semibold">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
                        <Input
                          id="register-email"
                          type="email"
                          placeholder="name@example.com"
                          value={registerForm.email}
                          onChange={(e) => handleRegisterChange('email', e.target.value)}
                          className={`pl-10 sm:pl-12 h-12 sm:h-14 rounded-xl border-2 transition-colors text-sm sm:text-base ${errors.email ? 'border-destructive' : 'border-border focus:border-primary'}`}
                          disabled={isLoading}
                        />
                        {errors.email && (
                          <div className="flex items-center mt-2 text-destructive text-xs font-medium">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {errors.email}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="register-password" className="text-sm font-semibold">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-muted-foreground" />
                        <Input
                          id="register-password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Create a password"
                          value={registerForm.password}
                          onChange={(e) => handleRegisterChange('password', e.target.value)}
                          className={`pl-10 sm:pl-12 pr-10 sm:pr-12 h-12 sm:h-14 rounded-xl border-2 transition-colors text-sm sm:text-base ${errors.password ? 'border-destructive' : 'border-border focus:border-primary'}`}
                          disabled={isLoading}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 h-9 w-9 sm:h-10 sm:w-10 rounded-lg hover:bg-muted"
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={isLoading}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" /> : <Eye className="h-4 w-4 sm:h-5 sm:w-5" />}
                        </Button>
                        {errors.password && (
                          <div className="flex items-center mt-2 text-destructive text-xs font-medium">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {errors.password}
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">Must be at least 6 characters</p>
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full h-12 sm:h-14 bg-gradient-to-r from-primary to-pink-500 hover:from-primary/90 hover:to-pink-500/90 text-primary-foreground rounded-xl shadow-lg shadow-primary/30 transition-all duration-300 hover:scale-[1.02] text-sm sm:text-base font-semibold"
                      disabled={isLoading}
                    >
                      {registerLoading ? (
                        <div className="flex items-center gap-2">
                          <Loader2 className="animate-spin h-4 w-4 sm:h-5 sm:w-5" />
                          <span>Creating Account...</span>
                        </div>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                          <span>Create Account</span>
                        </>
                      )}
                    </Button>
                  </form>
                </TabsContent>

                {/* Benefits */}
                <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-border">
                  <div className="grid grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" />
                      <span>5 free credits</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" />
                      <span>HD quality</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" />
                      <span>Voice synthesis</span>
                    </div>
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" />
                      <span>AI scripts</span>
                    </div>
                  </div>
                </div>
              </Tabs>
            </div>
          </CardContent>
        </div>
      </Card>
    </div>
  </div>
  );
};

export default AuthModal;
